import { useMemo, useState } from "react";
import { useAuth } from "react-oidc-context";
import { useQuery } from "@tanstack/react-query";
import { ChevronDown, ChevronRight, Search, UserPlus } from "lucide-react";
import { cn } from "@/lib/utils";
import { useAccessToken } from "@/auth/useAccessToken";
import { chatConnectClient, normalizeUserId, type ChatDm, type DirectoryUser, type PresenceEntry } from "./connectClient";
import { OPEN_DM_EVENT, type OpenDmDetail } from "./ChatDock";

/**
 * Gmail-style chat roster rendered inside the mail sidebar, below the
 * folder list. Shows people you've actually talked to (DMs started by
 * you or them) on top, ordered by most-recent + online presence. A "+"
 * search button lifts the existing ChatDock directory-search card so
 * you can discover someone new in the org.
 *
 * Scrollable: the outer container is `min-h-0` + the list is
 * `overflow-auto`, so the roster grows into remaining sidebar height
 * without ever pushing the folders section off screen.
 */
export function ChatRoster() {
    const token = useAccessToken();
    const auth = useAuth();
    const myUserId = normalizeUserId(auth.user?.profile.sub);

    // Three independent fetches. The roster can render with only a
    // subset — a tenant-users 500 shouldn't hide the DMs I've already
    // had, for instance — so we don't block on combined success.
    const dmsQuery = useQuery({
        queryKey: ["chat-roster-dms", token],
        queryFn: () => chatConnectClient.listDms(token),
        enabled: Boolean(token),
        refetchInterval: 15_000
    });
    const tenantQuery = useQuery({
        queryKey: ["chat-roster-tenant", token],
        queryFn: () => chatConnectClient.listTenantUsers(token, 200),
        enabled: Boolean(token),
        staleTime: 2 * 60_000
    });
    const presenceQuery = useQuery({
        queryKey: ["chat-roster-presence", token],
        queryFn: () => chatConnectClient.listPresence(token),
        enabled: Boolean(token),
        refetchInterval: 20_000
    });

    const rows = useMemo<RosterRow[]>(() => {
        const dms = dmsQuery.data ?? [];
        const tenant = tenantQuery.data ?? [];
        // Keys ALWAYS normalized (no-dash) so legacy DMs with dashed
        // participant ids still resolve. Presence + tenant lookup both
        // round-trip through normalizeUserId.
        const presence = new Map<string, string>(
            (presenceQuery.data ?? []).map((p) => [normalizeUserId(p.userId), p.status])
        );

        const tenantByEmail = new Map<string, DirectoryUser>();
        const tenantById = new Map<string, DirectoryUser>();
        for (const u of tenant) {
            if (u.email) tenantByEmail.set(u.email.toLowerCase(), u);
            tenantById.set(normalizeUserId(u.userId), u);
        }

        // Roster visibility rule: a user appears if they are the peer
        // of at least one existing DM. Everyone else is discoverable
        // via the search drawer but stays out of the default list —
        // the user asked for a "frequent contacts" view, not a dump
        // of the whole tenant.
        const seen = new Set<string>();
        const out: RosterRow[] = [];

        for (const dm of dms) {
            const peerIdRaw = otherParticipantId(dm, myUserId);
            if (!peerIdRaw) continue;
            const peerId = normalizeUserId(peerIdRaw);
            if (seen.has(peerId)) continue;
            seen.add(peerId);
            const user = tenantById.get(peerId) ?? null;
            out.push({
                key: peerId,
                dmId: dm.id,
                userId: peerIdRaw,
                displayName: user?.displayName ?? user?.email ?? peerIdRaw,
                email: user?.email ?? "",
                presence: presence.get(peerId) ?? "offline",
                // Latest DM first — the BFF doesn't timestamp the
                // participants list, so "most recent" really means
                // "last row the server returned for me" which is
                // ordered by UpdatedAt server-side.
                score: dms.length - dms.indexOf(dm)
            });
        }

        // Keep everyone online (even without a DM) visible too — that's
        // the "just came online" signal that matches the user's ask
        // ("availability matters"). Skip myself.
        for (const u of tenant) {
            const uId = normalizeUserId(u.userId);
            if (uId === myUserId) continue;
            if (seen.has(uId)) continue;
            const status = presence.get(uId);
            if (status !== "online") continue;
            seen.add(uId);
            out.push({
                key: uId,
                dmId: null,
                userId: u.userId,
                displayName: u.displayName ?? u.email ?? u.userId,
                email: u.email,
                presence: status,
                score: 0
            });
        }

        // Sort: online first, then DM-history score descending, then name.
        out.sort((a, b) => {
            const onA = a.presence === "online" ? 1 : 0;
            const onB = b.presence === "online" ? 1 : 0;
            if (onA !== onB) return onB - onA;
            if (a.score !== b.score) return b.score - a.score;
            return a.displayName.localeCompare(b.displayName);
        });

        return out;
    }, [dmsQuery.data, tenantQuery.data, presenceQuery.data, myUserId]);

    const [filter, setFilter] = useState("");
    const needle = filter.trim();
    const [open, setOpen] = useState(true);

    // Live directory search — fires once the user has typed 2+ chars.
    // Covers organisational users who aren't in the cached tenant list
    // (the cache caps at 200, and a new hire might not be there yet).
    // Debounced via react-query staleTime; keeps the last response
    // visible while the next one is in flight.
    const directoryQuery = useQuery({
        queryKey: ["chat-roster-directory", needle.toLowerCase(), token],
        queryFn: () => chatConnectClient.searchDirectory(needle, token),
        enabled: Boolean(token) && needle.length >= 2,
        staleTime: 15_000,
        placeholderData: (previous) => previous
    });

    // When the user types, expand the result set to every source we have:
    //   1. Current roster rows whose name/email matches
    //   2. Cached tenant users not in the roster
    //   3. Live directory search hits (covers anyone missed by 1+2)
    const filtered = useMemo<RosterRow[]>(() => {
        if (needle.length === 0) return rows;
        const n = needle.toLowerCase();

        const seen = new Map<string, RosterRow>();

        // 1. Existing roster rows that match — ranked highest.
        for (const r of rows) {
            if (
                r.displayName.toLowerCase().includes(n) ||
                (r.email ?? "").toLowerCase().includes(n)
            ) {
                seen.set(r.userId, r);
            }
        }

        // 2. Cached tenant users not already in the roster.
        for (const u of tenantQuery.data ?? []) {
            if (u.userId === myUserId) continue;
            if (seen.has(u.userId)) continue;
            const label = (u.displayName ?? u.email ?? "").toLowerCase();
            if (label.includes(n) || (u.email ?? "").toLowerCase().includes(n)) {
                seen.set(u.userId, {
                    key: u.userId,
                    dmId: null,
                    userId: u.userId,
                    displayName: u.displayName ?? u.email ?? u.userId,
                    email: u.email,
                    presence: "offline",
                    score: 0
                });
            }
        }

        // 3. Live directory hits (BFF-side match covers nickname / partial
        //    email / anyone newly added since the 200-user tenant cache).
        for (const u of directoryQuery.data ?? []) {
            if (u.userId === myUserId) continue;
            if (seen.has(u.userId)) continue;
            seen.set(u.userId, {
                key: u.userId,
                dmId: null,
                userId: u.userId,
                displayName: u.displayName ?? u.email ?? u.userId,
                email: u.email,
                presence: "offline",
                score: 0
            });
        }

        return Array.from(seen.values());
    }, [rows, needle, tenantQuery.data, directoryQuery.data, myUserId]);

    const openDm = (row: RosterRow) => {
        const detail: OpenDmDetail = { userId: row.userId };
        window.dispatchEvent(new CustomEvent(OPEN_DM_EVENT, { detail }));
        // Clear the filter so the roster snaps back to the frequent-
        // contacts view after the chat pops open.
        setFilter("");
    };

    return (
        // Part of the folder pane's single scrolling column (Folders → Tasks → Chats).
        <section className="mt-1 flex flex-col" aria-label="Chat roster">
            <header className="flex items-center">
                <button
                    type="button"
                    onClick={() => setOpen((o) => !o)}
                    aria-expanded={open}
                    className="flex min-w-0 flex-1 items-center gap-1.5 px-2 pb-1 pt-2.5 text-xs font-semibold text-[var(--mail-text-2)]"
                >
                    {open ? <ChevronDown className="h-3.5 w-3.5 shrink-0" /> : <ChevronRight className="h-3.5 w-3.5 shrink-0" />}
                    Chats
                </button>
                <button
                    type="button"
                    onClick={() => window.dispatchEvent(new CustomEvent("athavita:openChatSearch"))}
                    aria-label="Find someone new"
                    title="Find someone new"
                    className="mt-1.5 flex h-6 w-6 items-center justify-center rounded text-[var(--mail-text-3)] hover:bg-[var(--mail-hover)] hover:text-foreground"
                >
                    <UserPlus className="h-3.5 w-3.5" />
                </button>
            </header>
            {open ? (<>
            <div className="pb-1 pl-[22px] pr-1 pt-0.5">
                <div className="relative">
                    <Search className="pointer-events-none absolute left-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-[var(--mail-text-3)]" aria-hidden />
                    <input
                        type="search"
                        value={filter}
                        onChange={(e) => setFilter(e.target.value)}
                        placeholder="Search people"
                        aria-label="Search chat roster and directory"
                        className="h-7 w-full rounded border border-[var(--mail-line-strong)] bg-background pl-7 pr-2 text-xs focus:outline-none focus:ring-1 focus:ring-primary"
                    />
                </div>
            </div>
            <ul className="pb-1" role="list">
                {dmsQuery.isPending && tenantQuery.isPending ? (
                    <li className="py-2 pl-6 pr-2 text-xs text-muted-foreground">Loading contacts…</li>
                ) : filtered.length === 0 ? (
                    <li className="py-2 pl-6 pr-2 text-xs text-muted-foreground">
                        {needle.length >= 2 && directoryQuery.isFetching
                            ? "Searching…"
                            : needle.length > 0
                                ? (needle.length < 2
                                    ? "Keep typing — at least 2 letters to search the directory."
                                    : "No one matches that search.")
                                : rows.length === 0
                                    ? "No chats yet. Type a name above to find someone in your org."
                                    : "No matches."}
                    </li>
                ) : (
                    filtered.map((row) => {
                        const status = presenceLabel(row.presence);
                        const statusTitle = row.dmId ? `${status} · recent chat` : status;
                        return (
                            <li key={row.key}>
                                <button
                                    type="button"
                                    onClick={() => openDm(row)}
                                    aria-label={`Chat with ${row.displayName}, ${statusTitle}`}
                                    title={statusTitle}
                                    className="flex h-8 w-full min-w-0 items-center gap-2.5 rounded pl-6 pr-2 text-left text-sm text-foreground transition-colors hover:bg-[var(--mail-hover)] focus:outline-none focus-visible:ring-2 focus-visible:ring-primary"
                                >
                                    <span
                                        aria-hidden
                                        className="flex h-[22px] w-[22px] shrink-0 items-center justify-center rounded-full bg-[var(--mail-primary-soft)] text-[10px] font-semibold text-primary"
                                    >
                                        {initials(row.displayName)}
                                    </span>
                                    <span className="min-w-0 flex-1 truncate" title={row.email ? `${row.displayName} · ${row.email}` : row.displayName}>
                                        {row.displayName}
                                    </span>
                                    <span
                                        aria-label={status}
                                        title={statusTitle}
                                        className={cn("h-2 w-2 shrink-0 rounded-full", presenceDotClass(row.presence))}
                                    />
                                </button>
                            </li>
                        );
                    })
                )}
            </ul>
            </>) : null}
        </section>
    );
}

interface RosterRow {
    key: string;
    dmId: string | null;
    userId: string;
    displayName: string;
    email: string;
    presence: string;
    score: number;
}

function otherParticipantId(dm: ChatDm, me: string | undefined): string | null {
    if (!me) return null;
    const meN = normalizeUserId(me);
    const aN = normalizeUserId(dm.participantA);
    const bN = normalizeUserId(dm.participantB);
    if (aN && aN !== meN) return dm.participantA;
    if (bN && bN !== meN) return dm.participantB;
    return null;
}

function initials(name: string): string {
    const words = name.split(/\s+/).filter(Boolean);
    if (words.length === 0) return "?";
    if (words.length === 1) return words[0]!.slice(0, 2).toUpperCase();
    return (words[0]![0]! + words[words.length - 1]![0]!).toUpperCase();
}

/**
 * Presence → dot colour. Online green, Away amber, DND red, everything
 * else (offline / unknown / missing presence entry) grey. Matches the
 * palette used in MeetingsView + ChatDock so the colour means the same
 * thing everywhere in the product.
 */
function presenceDotClass(presence: string): string {
    switch (presence) {
        case "online": return "bg-[#13a10e]";
        case "away": return "bg-[#eaa300]";
        case "dnd":
        case "busy": return "bg-[#d13438]";
        default: return "bg-transparent ring-1 ring-inset ring-[var(--mail-text-3)]";
    }
}

function presenceLabel(presence: string): string {
    switch (presence) {
        case "online": return "Available";
        case "away": return "Away";
        case "dnd": return "Do not disturb";
        case "busy": return "Busy";
        default: return "Offline";
    }
}

// Keep the unused-types import warning quiet while `PresenceEntry` is
// referenced only via the fetch return type — the type narrowing above
// reads straight from `presenceQuery.data`.
export type _RosterPresence = PresenceEntry;
